import React from "react";
import {
  LayoutGrid, Image as ImageIcon,
  Film, Download, Sparkles, Users, MapPin, Box, Edit2,
  Plus, Check, Grid3x3, List, Eye, Workflow, RefreshCw, Save, Activity
} from "lucide-react";
import { Section, DemoButton, TipBox, DemoTag, DemoNavItem } from "./HelpComponents";

const HelpStoryboard = ({ theme, expandedSection, toggleSection }) => {
  return (
    <div className="space-y-4">
      {/* 概述 */}
      <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
          分镜编辑器是平台的核心工作区，用于编辑分镜表、生成图片和视频。支持<strong style={{ color: "var(--color-text-primary)" }}>四种视图模式</strong>和<strong style={{ color: "var(--color-text-primary)" }}>批量操作</strong>。
        </p>
      </div>

      {/* 1. 视图模式 */}
      <Section id={200} title="四种视图模式" icon={LayoutGrid} expanded={expandedSection === 200} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            工具栏左侧有四个视图切换按钮，每种视图适合不同的工作场景：
          </p>
          {/* 视图切换器 */}
          <div className="flex items-center gap-2 p-2 rounded-lg border" style={{ backgroundColor: "var(--color-bg-tertiary)", borderColor: "var(--color-border-primary)" }}>
            <button className="p-2 rounded-lg" style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary }}>
              <List size={16} />
            </button>
            <button className="p-2 rounded-lg" style={{ color: "var(--color-text-muted)" }}>
              <Grid3x3 size={16} />
            </button>
            <button className="p-2 rounded-lg" style={{ color: "var(--color-text-muted)" }}>
              <Eye size={16} />
            </button>
            <button className="p-2 rounded-lg" style={{ color: "var(--color-text-muted)" }}>
              <Workflow size={16} />
            </button>
            <span className="text-xs ml-2" style={{ color: "var(--color-text-muted)" }}>← 点击切换视图</span>
          </div>

          {/* 列表视图 */}
          <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-2 mb-3">
              <List size={16} style={{ color: theme.colors.accentPrimary }} />
              <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>列表视图（默认）</span>
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--color-text-muted)" }}>
              详细卡片布局，显示所有编辑字段。适合精细编辑分镜内容。
            </p>
            <div className="space-y-2">
              {[1, 2].map(i => (
                <div key={i} className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)" }}>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded flex items-center justify-center text-xs font-bold" style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>#{i}</div>
                    <div className="flex-1 space-y-1">
                      <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>画面描述</div>
                      <div className="text-xs" style={{ color: "var(--color-text-secondary)" }}>青云山山门前，林小凡跪地求见...</div>
                    </div>
                    <div className="w-24 rounded" style={{ aspectRatio: "16/9", backgroundColor: "var(--color-bg-overlay)" }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 网格视图 */}
          <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-2 mb-3">
              <Grid3x3 size={16} style={{ color: theme.colors.accentPrimary }} />
              <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>网格视图</span>
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--color-text-muted)" }}>
              缩略图网格，快速浏览所有分镜。适合查看整体效果和批量选择。
            </p>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="rounded-lg border overflow-hidden" style={{ borderColor: "var(--color-border-primary)" }}>
                  <div className="bg-[var(--color-bg-tertiary)]" style={{ aspectRatio: "16/9" }}></div>
                  <div className="px-2 py-1 text-2xs text-center" style={{ backgroundColor: "var(--color-bg-secondary)", color: "var(--color-text-muted)" }}>#{i}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 画廊视图 */}
          <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-2 mb-3">
              <Eye size={16} style={{ color: theme.colors.accentPrimary }} />
              <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>画廊视图</span>
            </div>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              全屏图片轮播，左右切换查看。适合审阅成片效果。
            </p>
          </div>

          {/* 流程视图 */}
          <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-2 mb-3">
              <Workflow size={16} style={{ color: theme.colors.accentPrimary }} />
              <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>流程视图</span>
            </div>
            <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              节点式可视化流程图，支持拖拽调整顺序。适合规划分镜结构。
            </p>
          </div>
        </div>
      </Section>

      {/* 2. 工具栏功能 */}
      <Section id={201} title="工具栏功能详解" icon={Sparkles} expanded={expandedSection === 201} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            工具栏位于分镜编辑器顶部，包含所有核心操作按钮：
          </p>

          {/* 工具栏模拟 */}
          <div className="p-3 rounded-xl border space-y-3" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-2 flex-wrap">
              <DemoButton icon={ImageIcon} label="一键生图" theme={theme} size="sm" />
              <DemoButton icon={Film} label="一键生视频" theme={theme} size="sm" variant="purple" />
              <DemoButton icon={Film} label="序列视频" theme={theme} size="sm" color={theme.colors.accentPrimary} />
              <DemoButton icon={Download} label="下载视频" theme={theme} size="sm" variant="green" />
              <DemoButton icon={Sparkles} label="工业化" theme={theme} size="sm" variant="secondary" />
              <DemoButton icon={Activity} label="系统监控" theme={theme} size="sm" variant="secondary" />
              <DemoButton icon={RefreshCw} label="刷新" theme={theme} size="sm" variant="secondary" />
              <DemoButton icon={Save} label="保存" theme={theme} size="sm" variant="secondary" />
            </div>
          </div>

          {/* 按钮说明 */}
          <div className="space-y-3">
            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-1">
                <ImageIcon size={14} style={{ color: theme.colors.accentPrimary }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>一键生图</span>
              </div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                为选中的分镜批量生成图片。需先在左侧选择 AI 模型和画风。
              </p>
            </div>

            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Film size={14} style={{ color: theme.colors.purplePrimary }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>一键生视频</span>
              </div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                为选中的分镜批量生成视频。需先完成生图。
              </p>
            </div>

            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Film size={14} style={{ color: theme.colors.accentPrimary }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>序列视频</span>
              </div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                首尾帧连续模式，让相邻镜头的视频衔接更流畅。需按顺序选择多个分镜。
              </p>
            </div>

            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Download size={14} style={{ color: theme.colors.success }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>下载视频</span>
              </div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                批量下载选中分镜的视频文件。
              </p>
            </div>

            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} style={{ color: "var(--color-text-muted)" }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>工业化</span>
              </div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                批量生成网格分镜（如 2x2、3x3），用于快速生成多角度镜头。
              </p>
            </div>

            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Activity size={14} style={{ color: "var(--color-text-muted)" }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>系统监控</span>
              </div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                查看当前生成任务的进度和队列状态。
              </p>
            </div>

            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-1">
                <RefreshCw size={14} style={{ color: "var(--color-text-muted)" }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>刷新资产</span>
              </div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                重新加载角色/场景/道具列表，用于同步最新的资产数据。
              </p>
            </div>

            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Save size={14} style={{ color: "var(--color-text-muted)" }} />
                <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>保存</span>
              </div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                手动保存分镜表。系统会自动保存，但重要修改后建议手动保存一次。
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* 3. 资产快速添加栏 */}
      <Section id={202} title="资产快速添加栏" icon={Plus} expanded={expandedSection === 202} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            工具栏下方有一排资产快速添加按钮，用于批量为分镜添加角色/场景/道具引用：
          </p>

          {/* 模拟快速添加栏 */}
          <div className="p-3 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${theme.colors.accentPrimary}20`, border: `1px solid ${theme.colors.accentPrimary}40` }}>
                <Check size={14} style={{ color: theme.colors.accentPrimary }} />
                <span className="text-xs font-bold" style={{ color: theme.colors.accentPrimary }}>已选 3 个分镜</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <Users size={14} style={{ color: "var(--color-text-primary)" }} />
                <span className="text-xs" style={{ color: "var(--color-text-primary)" }}>角色</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <MapPin size={14} style={{ color: "var(--color-text-primary)" }} />
                <span className="text-xs" style={{ color: "var(--color-text-primary)" }}>场景</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer" style={{ borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-tertiary)" }}>
                <Box size={14} style={{ color: "var(--color-text-primary)" }} />
                <span className="text-xs" style={{ color: "var(--color-text-primary)" }}>道具</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: theme.colors.accentPrimary, color: "white" }}>1</div>
              <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>勾选需要添加资产的分镜</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: theme.colors.accentPrimary, color: "white" }}>2</div>
              <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>点击"角色/场景/道具"按钮，在下拉菜单中选择资产</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: theme.colors.accentPrimary, color: "white" }}>3</div>
              <span className="text-sm" style={{ color: "var(--color-text-primary)" }}>选中的资产会自动添加到所有勾选的分镜中</span>
            </div>
          </div>

          <TipBox theme={theme}>批量添加资产后，每个分镜仍可单独调整引用的资产和变体。</TipBox>
        </div>
      </Section>

      {/* 4. 单条分镜编辑 */}
      <Section id={203} title="单条分镜编辑字段" icon={Edit2} expanded={expandedSection === 203} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            在列表视图中，每个分镜卡片包含以下可编辑字段：
          </p>

          {/* 模拟分镜卡片 */}
          <div className="p-4 rounded-xl border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="font-bold mb-1" style={{ color: "var(--color-text-muted)" }}>镜号</div>
                <div style={{ color: "var(--color-text-primary)" }}>#1</div>
              </div>
              <div>
                <div className="font-bold mb-1" style={{ color: "var(--color-text-muted)" }}>景别</div>
                <div style={{ color: "var(--color-text-primary)" }}>全景</div>
              </div>
              <div className="col-span-2">
                <div className="font-bold mb-1" style={{ color: "var(--color-text-muted)" }}>画面描述</div>
                <div style={{ color: "var(--color-text-secondary)" }}>青云山山门前，林小凡跪地求见...</div>
              </div>
              <div className="col-span-2">
                <div className="font-bold mb-1" style={{ color: "var(--color-text-muted)" }}>对白</div>
                <div style={{ color: "var(--color-text-secondary)" }}>林小凡：弟子林小凡，求见掌门！</div>
              </div>
              <div className="col-span-2">
                <div className="font-bold mb-1" style={{ color: "var(--color-text-muted)" }}>动作</div>
                <div style={{ color: "var(--color-text-secondary)" }}>林小凡跪地，双手抱拳</div>
              </div>
              <div className="col-span-2">
                <div className="font-bold mb-1" style={{ color: "var(--color-text-muted)" }}>引用资产</div>
                <div className="flex gap-1.5">
                  <DemoTag label="林小凡" theme={theme} />
                  <DemoTag label="青云山山门" color={theme.colors.info} theme={theme} />
                </div>
              </div>
            </div>
          </div>

          {/* 字段说明 */}
          <div className="space-y-2">
            <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="text-xs font-bold mb-1" style={{ color: "var(--color-text-primary)" }}>画面描述（必填）</div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                AI 生图的核心提示词，描述镜头中的场景、人物、动作、氛围等。
              </p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="text-xs font-bold mb-1" style={{ color: "var(--color-text-primary)" }}>对白（可选）</div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                角色台词，用于后期配音或字幕。
              </p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="text-xs font-bold mb-1" style={{ color: "var(--color-text-primary)" }}>动作（可选）</div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                角色的具体动作描述，补充画面描述。
              </p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="text-xs font-bold mb-1" style={{ color: "var(--color-text-primary)" }}>景别（可选）</div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                镜头类型：特写、近景、中景、全景、远景等。
              </p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--color-bg-secondary)" }}>
              <div className="text-xs font-bold mb-1" style={{ color: "var(--color-text-primary)" }}>引用资产</div>
              <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                选择该镜头中出现的角色、场景、道具。AI 会根据资产的定妆照生成一致的画面。
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* 5. 批量操作 */}
      <Section id={204} title="批量操作技巧" icon={Check} expanded={expandedSection === 204} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            分镜编辑器支持多选分镜进行批量操作，大幅提升工作效率：
          </p>

          <div className="space-y-3">
            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="text-sm font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>如何多选分镜</div>
              <ul className="space-y-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
                <li>• 点击分镜卡片左上角的复选框</li>
                <li>• 或点击工具栏的"全选"按钮</li>
                <li>• 选中后卡片会高亮显示</li>
              </ul>
            </div>

            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <div className="text-sm font-bold mb-2" style={{ color: "var(--color-text-primary)" }}>批量操作清单</div>
              <ul className="space-y-1 text-xs" style={{ color: "var(--color-text-muted)" }}>
                <li>• 批量生图：一次性为多个分镜生成图片</li>
                <li>• 批量生视频：一次性为多个分镜生成视频</li>
                <li>• 批量添加资产：通过快速添加栏为多个分镜添加角色/场景/道具</li>
                <li>• 批量下载：一次性下载多个分镜的视频文件</li>
                <li>• 批量删除：删除选中的分镜</li>
              </ul>
            </div>
          </div>

          <TipBox theme={theme} type="success">
            建议工作流程：先批量生图 → 检查效果 → 批量生视频 → 批量下载。
          </TipBox>
        </div>
      </Section>

      {/* 6. 画布模式 */}
      <Section id={205} title="画布模式" icon={Workflow} expanded={expandedSection === 205} onToggle={toggleSection} theme={theme}>
        <div className="space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            画布模式是一个独立的可视化编辑器，在侧边栏点击"画布模式"进入：
          </p>
          <DemoNavItem icon={Workflow} label="画布模式" active={true} theme={theme} />
          <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>
            画布模式提供自由拖拽的节点编辑体验，每个分镜是一个节点，可以：
          </p>
          <ul className="space-y-2 text-sm" style={{ color: "var(--color-text-secondary)" }}>
            <li>• 拖拽节点调整分镜顺序</li>
            <li>• 缩放画布查看全局结构</li>
            <li>• 点击节点查看/编辑分镜详情</li>
            <li>• 连线显示分镜之间的逻辑关系</li>
          </ul>
          <TipBox theme={theme}>画布模式适合规划复杂的分镜结构，但日常编辑建议使用列表视图。</TipBox>
        </div>
      </Section>
    </div>
  );
};

export default HelpStoryboard;
